import { useState } from 'react'
import Image from 'next/image'
import { LeadFormDialog } from '~widgets/lead-form/components/lead-form-dialog'
import personImg from './images/managment-for-psy.jpg'
import giftImg from './images/gift.png'
import { cn } from '~shared/utils/cn'

// ─── Types ────────────────────────────────────────────────────────────────────

type TProgramMarketingForPsyProps = {
  institute?: string | null
  uid?: number | null
  name?: string | null
}

// ─── Constants ────────────────────────────────────────────────────────────────

const BADGE = 'Курс в подарок'
const TITLE = 'Маркетинг для психологов'
const DESCRIPTION =
  'Разберем все аспекты эффективного и этичного продвижения психологических услуг'
const BULLETS = [
  'Как побороть страх осуждения и синдром самозванца',
  'Как уверенно говорить о своих услугах и их стоимости',
  'Особенности продвижения через агрегаторы',
  'Почему важно уметь говорить на камеру и как создавать продающий контент',
  'Построение личного бренда и продвижение через социальные сети'
]
const PRICE_FREE = 'Бесплатно'
const PRICE_OLD = '39 000 ₽'
const BUTTON = 'Получить курс в подарок'

// ─── FireIcon ─────────────────────────────────────────────────────────────────

const FireIcon = () => (
  <svg
    width='16'
    height='20'
    viewBox='0 0 16 20'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M9.50242 0.855348C9.50025 0.779128 9.41708 0.729498 9.35162 0.768604C5.88626 2.83887 5.95044 8.03219 5.99181 8.9732C5.99493 9.04418 5.92777 9.09427 5.86349 9.064C5.46316 8.87546 4.3161 8.16541 4.25273 6.19842C4.25028 6.12209 4.16792 6.07302 4.10223 6.11195C2.09578 7.30113 0.75 9.49749 0.75 11.9331C0.75 15.661 3.884 18.6831 7.75 18.6831C11.616 18.6831 14.75 15.661 14.75 11.9331C14.75 6.51723 9.6303 5.3525 9.50242 0.855348Z'
      fill='white'
      stroke='white'
      strokeWidth='1.5'
    />
  </svg>
)

// ─── Component ────────────────────────────────────────────────────────────────

export const ProgramMarketingForPsySection = ({
  institute,
  uid,
  name
}: TProgramMarketingForPsyProps) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <section className='py-[56px] 2lg:py-[80px]'>
      <div className='mx-auto px-[16px] box-border max-w-[672px] md:max-w-[864px] 2lg:max-w-[1248px]'>
        <div className='relative bg-[#F2F2F2] rounded-[24px] md:rounded-[32px] 2lg:rounded-[40px] p-[24px] md:p-[40px] 2lg:p-[56px] overflow-hidden'>
          {/* ─── Badge ─── */}
          <div className='inline-flex items-center gap-[8px] bg-[#7D5EE4] text-white px-[16px] py-[8px] rounded-[55px] mb-[24px] md:mb-[32px]'>
            <FireIcon />
            <span className='text-[14px] md:text-[16px] font-medium leading-[120%]'>
              {BADGE}
            </span>
          </div>

          {/* ─── Desktop layout: content left, image right ─── */}
          <div className='grid lg:grid-cols-[2fr_1fr] grid-cols-1 gap-[24px] 2lg:items-start 2lg:gap-[48px] items-stretch'>
            {/* Left: text content */}
            <div className='flex-1'>
              <h2 className='text-[32px] md:text-[40px] 2lg:text-[48px] font-semibold leading-[120%] mb-[16px] md:mb-[24px]'>
                {TITLE}
              </h2>
              <p className='text-[16px] md:text-[18px] 2lg:text-[20px] leading-[140%] mb-[16px] md:mb-[24px]'>
                {DESCRIPTION}
              </p>
              <ul className='flex flex-col gap-[8px] mb-[24px] md:mb-[32px]'>
                {BULLETS.map((bullet, idx) => (
                  <li
                    key={idx}
                    className='flex items-start gap-[8px] text-[14px] md:text-[16px] leading-[140%]'>
                    <span className='shrink-0'>•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>

              {/* Price row */}
              <div className='flex items-end gap-[8px] mb-[16px] md:mb-[24px] relative'>
                <span className='text-[24px] md:text-[40px] font-semibold leading-[100%]'>
                  {PRICE_FREE}
                </span>
                <span className='text-[14px] md:text-[20px] leading-[120%] line-through text-[#1D1D1D]'>
                  {PRICE_OLD}
                </span>
                {/* Gift image — desktop: absolute over price row */}
              </div>

              {/* Button */}
              <div className='relative flex'>
                <button
                  onClick={() => setIsOpen(true)}
                  className={cn(
                    'w-full lg:w-auto md:min-w-[320px] z-10 2lg:min-w-[360px] py-[20px] px-[40px] rounded-[16px] text-white text-[16px] md:text-[18px] font-medium leading-[120%] transition-opacity hover:opacity-90'
                  )}
                  style={{
                    background:
                      'linear-gradient(90deg, #000000 0%, #7D5EE4 100%)'
                  }}>
                  {BUTTON}
                </button>
                <div
                  className={cn(
                    'absolute w-[100px] pointer-events-none select-none right-0 top-[-80px]',
                    'md:right-[70px] md:top-[-100px] md:w-[150px]',
                    'lg:w-[200px] lg:right-[50px] lg:top-[-50px]',
                    '2lg:w-[300px] 2lg:right-[100px] 2lg:top-[-100px] z-1'
                  )}>
                  <Image
                    src={giftImg}
                    alt='gift'
                    className='w-full h-auto object-contain'
                    quality={90}
                  />
                </div>
              </div>
            </div>

            {/* Right: person image — desktop only */}
            <div className='rounded-[24px] overflow-hidden shrink-0 h-full'>
              <Image
                src={personImg}
                alt='Маркетинг для психологов'
                className={cn(
                  'w-full h-full  object-cover rounded-[24px] object-left-[100px]',
                  'lg:object-[-200px_center]'
                )}
                quality={90}
              />
            </div>
          </div>
        </div>
      </div>

      <LeadFormDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        program={{
          productId: uid ?? null,
          productName: name ?? '',
          productCategory: 'Курсы',
          productFaculty: 'Психология',
          institute: institute ?? ''
        }}
      />
    </section>
  )
}
